import { isReservedBranchSlug, isReservedWorkspaceSlug, PARISH_BRANCH_SLUG } from "@/shared/lib/routing/reserved-slugs"

export function normalizePathname(pathname: string) {
  if (!pathname) return "/"
  const [path] = pathname.split("?")
  const normalized = path.startsWith("/tenant/") ? path.slice("/tenant".length) || "/" : path === "/tenant" ? "/" : path
  if (normalized.length > 1 && normalized.endsWith("/")) {
    return normalized.slice(0, -1)
  }
  return normalized
}

function toSegments(normalized: string): string[] {
  return normalized.split("/").filter(Boolean)
}

/**
 * Parsed tenant location under the flat URL model (IBA-155):
 *   - `/:ws`                 → Organization context (branch resolves to the SYSTEM/parish scope)
 *   - `/:ws/<org-route>`     → Organization context (second segment is a reserved Organization/admin route)
 *   - `/:ws/:branch/...`     → Branch context
 */
export type TenantRouteContext = {
  workspaceSlug: string | null
  /** Branch slug for header/context resolution — `parish` for the Organization context. */
  branchSlug: string | null
  /** True when the URL carries no explicit branch segment (Organization context). */
  isOrganization: boolean
  /** Path relative to the context prefix, e.g. `/members`, `/finance/transactions`, `/`. */
  relativePath: string
}

function relativeFrom(segments: string[], startIndex: number): string {
  const tail = segments.slice(startIndex)
  return tail.length ? `/${tail.join("/")}` : "/"
}

/**
 * Older links could contain the database slug of the workspace SYSTEM branch.
 * That branch is an implementation detail: public tenant URLs always represent
 * the parish as the workspace-root form (`/:workspace/...`).
 */
export function isLegacyInternalSystemBranchSlug(branchSlug: string | null | undefined) {
  const normalized = branchSlug?.trim().toLowerCase()
  return Boolean(
    normalized &&
      (normalized.startsWith("systembranch") || normalized.startsWith("system-branch")),
  )
}

export function resolveTenantRouteContext(pathname: string): TenantRouteContext {
  const normalized = normalizePathname(pathname)
  const segments = toSegments(normalized)
  const workspaceSlug = segments[0]
  if (!workspaceSlug || isReservedWorkspaceSlug(workspaceSlug)) {
    return { workspaceSlug: null, branchSlug: null, isOrganization: false, relativePath: normalized }
  }

  const second = segments[1]
  // Organization context: no branch segment, or the second segment is an Organization/admin route name.
  if (!second || isReservedBranchSlug(second)) {
    return {
      workspaceSlug,
      branchSlug: PARISH_BRANCH_SLUG,
      isOrganization: true,
      relativePath: relativeFrom(segments, 1),
    }
  }

  // Normalize a stale internal SYSTEM-branch URL before any route guard can
  // mistake it for a normal branch that needs a UserBranchAccess assignment.
  if (isLegacyInternalSystemBranchSlug(second)) {
    return {
      workspaceSlug,
      branchSlug: PARISH_BRANCH_SLUG,
      isOrganization: true,
      relativePath: relativeFrom(segments, 2),
    }
  }

  // Branch context.
  return {
    workspaceSlug,
    branchSlug: second,
    isOrganization: false,
    relativePath: relativeFrom(segments, 2),
  }
}

export function extractWorkspaceSlugFromAdminPath(pathname: string) {
  return resolveTenantRouteContext(pathname).workspaceSlug
}

export function extractBranchSlugFromAdminPath(pathname: string) {
  return resolveTenantRouteContext(pathname).branchSlug
}

export function isParishBranchAlias(branchSlug: string | null | undefined) {
  return branchSlug?.trim().toLowerCase() === PARISH_BRANCH_SLUG
}

/**
 * Context-relative path: `/members`, `/settings`, `/` for the context home.
 * Strips the `/:workspaceSlug/:branchSlug` (branch) or `/:workspaceSlug` (organization) prefix.
 */
export function toCanonicalAdminPath(pathname: string) {
  const location = resolveTenantRouteContext(pathname)
  if (!location.workspaceSlug) return normalizePathname(pathname)
  return location.relativePath
}

/**
 * Normalizes the path segment passed to {@link buildBranchAdminUrl} (`/members/...`).
 * Does not pass the fragment through {@link toCanonicalAdminPath}: paths like `/finance/transfers/create` share
 * the same segment shape as `/:workspace/:branch/...` and would be corrupted.
 */
function normalizeBranchAdminRelativePath(rawPath: string): string {
  const base =
    rawPath === "" || rawPath === "/"
      ? ""
      : rawPath.startsWith("/")
        ? rawPath
        : `/${rawPath}`

  return base === "/" ? "" : base
}

function splitPathAndSuffix(adminPath: string): { pathPart: string; suffix: string } {
  const suffixIndex = adminPath.search(/[?#]/)
  const rawPath = suffixIndex >= 0 ? adminPath.slice(0, suffixIndex) : adminPath
  const suffix = suffixIndex >= 0 ? adminPath.slice(suffixIndex) : ""
  const pathPart = normalizeBranchAdminRelativePath(rawPath === "" ? "/" : rawPath)
  return { pathPart, suffix }
}

/**
 * Builds a tenant admin URL under the flat model. When `branchSlug` is the parish alias, the URL collapses to
 * the Organization form `/:workspaceSlug/...` (no branch segment); otherwise `/:workspaceSlug/:branchSlug/...`.
 */
export function buildBranchAdminUrl(
  branchSlug: string | null | undefined,
  adminPath = "/",
  workspaceSlug?: string | null,
) {
  if (!branchSlug) {
    return "/dashboard"
  }
  const { pathPart, suffix } = splitPathAndSuffix(adminPath)

  if (isParishBranchAlias(branchSlug)) {
    // Organization context lives at the workspace root; a workspace slug is required to address it.
    return workspaceSlug ? `/${workspaceSlug}${pathPart}${suffix}` : "/dashboard"
  }

  return workspaceSlug
    ? `/${workspaceSlug}/${branchSlug}${pathPart}${suffix}`
    : `/${branchSlug}${pathPart}${suffix}`
}

/**
 * Organization ("Settings"/parish scope) URL: `/:workspaceSlug/...`. The Organization context is the workspace
 * root under the flat model; branch-scoped Organization data resolves to the SYSTEM/parish branch server-side.
 */
export function buildOrganizationUrl(workspaceSlug: string | null | undefined, path = "/") {
  if (!workspaceSlug) {
    return "/dashboard"
  }
  const { pathPart, suffix } = splitPathAndSuffix(path)
  return `/${workspaceSlug}${pathPart}${suffix}`
}

export function buildCanonicalBranchAdminUrl(
  workspaceSlug: string | null | undefined,
  branchSlug: string | null | undefined,
  adminPath = "/",
) {
  if (!workspaceSlug || !branchSlug) {
    return "/dashboard"
  }

  const { pathPart, suffix } = splitPathAndSuffix(adminPath)

  if (isParishBranchAlias(branchSlug)) {
    return `/${workspaceSlug}${pathPart}${suffix}`
  }

  return `/${workspaceSlug}/${branchSlug}${pathPart}${suffix}`
}
